import { createAsyncThunk, ThunkDispatch } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { WatchQueryFetchPolicy } from "@apollo/client";
import { RootState } from "../../app/store";
import { apolloClient } from "../../apollo/graphql";
import {
  MAX_EMPLOYEE_ATTACHMENT_BYTES,
  MAX_EMPLOYEE_VIDEO_BYTES,
} from "../../constants/uploadLimits";
import { POPUP_EXCLUDED_MESSAGES } from "../notifications/notificationAudiences";
import {
  AgeCategory,
  Complexion,
  CookingExperience,
  CountriesList,
  CurrencyTypes,
  Education,
  EducationLevel,
  Employee,
  EmployeeArgs,
  EmployeeStatus,
  EmployeesWithCount,
  ExperienceYears,
  LanguageExperience,
  MaritalStatus,
  MedicalStatus,
  Nationalities,
  NotificationArgs,
  NotificationsWithCount,
  NotificationAudience,
  NotificationStatus,
  PhotoTypes,
  ProcessState,
  ProfessionalTypes,
  ReligionTypes,
  RequiredDocuments,
  Sex,
  ShareByEmail,
  ShortLink,
  Skills,
  UpdateEmployeeStatusInput,
  UpdateEmployeeFeesInput,
  AddEmployeeStatusRemarkInput,
  BulkUpdateEmployeeStatusInput,
  BulkStatusUpdateResult,
  UpdateBookingFollowUpInput,
  BulkUpdateBookingFollowUpInput,
  BulkBookingFollowUpResult,
  WafidMedicalCheckResult,
  WafidMedicalBulkResult,
  BookingFollowUpType,
  BookingFollowUpStatus,
  Visa,
  VisaArgs,
  VisaStatus,
  Notification,
  WorkExperience,
} from "./types/processTypes";
import {
  CHECK_STATUS_CAPTCHA,
  GET_ALL_EMPLOYEES,
  GET_ALL_NOTIFICATIONS,
  GET_ALL_SHORT_LINKS,
  GET_ALL_VISAS,
  GET_LINKED_EMPLOYEES,
  GET_SELECTED_EMPLOYEE,
  GET_SELECTED_VISA,
  GET_SHARED_EMPLOYEES,
} from "../../apollo/queries";
import {
  RecaptchaCheckStatus,
  RejectWithValueType,
  UploadGraphQL,
  UploadGraphQLResult,
} from "../auth/types/authType";
import {
  ADD_UPDATE_EMPLOYEE,
  UPDATE_EMPLOYEE_STATUS,
  UPDATE_EMPLOYEE_FEES,
  ADD_EMPLOYEE_STATUS_REMARK,
  BULK_UPDATE_EMPLOYEE_STATUS,
  UPDATE_BOOKING_FOLLOW_UP,
  BULK_UPDATE_BOOKING_FOLLOW_UP,
  CHECK_WAFID_MEDICAL,
  BULK_CHECK_WAFID_MEDICAL,
  ADD_UPDATE_NOTIFICATION,
  ADD_UPDATE_VISA,
  GENERATE_LINK,
  LINK_EMPLOYEE_VISA,
  MARK_AS_READ_NOTIFICATION,
  MARK_ALL_AS_READ_NOTIFICATIONS,
  REMOVE_EMPLOYEE,
  REMOVE_NOTIFICATION,
  REMOVE_VISA,
  SEND_WHATSAPP_DOCUMENT,
  SHARE_BY_EMAIL,
  SINGLE_UPLOAD_MUTATION,
  CLEAR_EMPLOYEE_ATTACHMENT,
  UPLOAD_FILE,
} from "../../apollo/mutations";
import { Address, Company } from "../setups/types/setupTypes";
import { addYears } from "date-fns";

/** Called from list-affecting mutation thunks so cache-first list fetches for other tabs/filters can't serve pre-mutation data. */
function evictEmployeesListCache() {
  apolloClient.cache.evict({ id: "ROOT_QUERY", fieldName: "employees" });
  apolloClient.cache.gc();
}

export const shareByEmail = createAsyncThunk<
  any,
  ShareByEmail,
  { rejectValue: RejectWithValueType }
>("processes/shareByEmail", async (employeeArgs, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const { email, link } = employeeArgs;
    const response = await apolloClient.mutate({
      mutation: SHARE_BY_EMAIL,
      variables: { email, link },
    });

    if (response && response.data && response.data.shareLinkByEmail) {
      return response.data.shareLinkByEmail as ShareByEmail;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const linkedEmployees = createAsyncThunk<
  any,
  EmployeeArgs,
  { rejectValue: RejectWithValueType }
>("processes/linkedEmployees", async (employeeArgs, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const { refreshList, agencyId, agentId, status } = employeeArgs;
    const fetchPolicy =
      refreshList === "refresh" ? "network-only" : "cache-first";
    console.log(agencyId, agentId);
    const response = await apolloClient.query({
      query: GET_LINKED_EMPLOYEES,
      variables: { agencyId, agentId, status },
      fetchPolicy,
    });

    if (response && response.data && response.data.linkedEmployees) {
      //console.log(response.data.linkedEmployees);
      return response.data.linkedEmployees as EmployeesWithCount;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const sharedEmployees = createAsyncThunk<
  any,
  EmployeeArgs,
  { rejectValue: RejectWithValueType }
>("processes/sharedEmployees", async (employeeArgs, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const { refreshList, link } = employeeArgs;
    const fetchPolicy =
      refreshList === "refresh" ? "network-only" : "cache-first";
    const response = await apolloClient.query({
      query: GET_SHARED_EMPLOYEES,
      variables: { link },
      fetchPolicy,
    });

    if (response && response.data && response.data.sharedEmployees) {
      return response.data.sharedEmployees as EmployeesWithCount;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const generateLink = createAsyncThunk<
  any,
  ShortLink,
  { rejectValue: RejectWithValueType }
>("processes/generateLink", async (arg, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: GENERATE_LINK,
      variables: {
        ids: arg.employeeIds,
        headerId: arg.headerId,
      },
    });

    if (response && response.data && response.data.generateLink) {
      return response.data.generateLink as ShortLink;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const fetchShortLinks = createAsyncThunk<
  any,
  string,
  { rejectValue: RejectWithValueType }
>("processes/fetchShortLinks", async (employeeArgs, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;

  try {
    const response = await apolloClient.query({
      query: GET_ALL_SHORT_LINKS,
    });

    if (response && response.data && response.data.shortLinks) {
      return response.data.shortLinks as ShortLink[];
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const fetchEmployees = createAsyncThunk<
  any,
  EmployeeArgs,
  { rejectValue: RejectWithValueType }
>("processes/fetchEmployees", async (employeeArgs, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;

  try {
    const { refreshList, ...variables } = employeeArgs;
    const fetchPolicy: WatchQueryFetchPolicy =
      refreshList === "refresh" || refreshList === "network-only"
        ? "network-only"
        : "cache-first";
    const response = await apolloClient.query({
      query: GET_ALL_EMPLOYEES,
      variables,
      fetchPolicy,
    });

    if (response && response.data && response.data.employees) {
      return response.data.employees as EmployeesWithCount;
    }
    return rejectWithValue({ message: "No employees data received" });
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const getEmployee = createAsyncThunk<
  any,
  string,
  { rejectValue: RejectWithValueType }
>("processes/getEmployee", async (employeeId, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.query({
      query: GET_SELECTED_EMPLOYEE,
      variables: { id: employeeId },
    });
    if (response && response.data && response.data.getEmployee) {
      return response.data.getEmployee as Employee;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

/** GraphQL CompanyInput requires displayName + email; omit incomplete refs. */
function toCompanyInput(company?: Company | null) {
  if (!company?._id) return undefined;
  const displayName = company.displayName?.trim();
  const email = company.email?.trim();
  if (!displayName || !email) return undefined;
  return {
    _id: company._id,
    type: company.type,
    displayName,
    email,
  };
}

export const createEmployee = createAsyncThunk<
  any,
  Employee,
  { rejectValue: RejectWithValueType }
>("processes/createEmployee", async (employee, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const emp = { ...employee } as Employee;

  //console.log(employee._id);
  const {
    address,
    contactPerson,
    education,
    visa,
    workExperience,
    additionalData,
    documents,
    owner,
    partner,
    parentCompany,
    prospects,
    embassyProcess,
    flight,
  } = emp;
  let enjazNumber, stampedDate;
  if (embassyProcess) {
    enjazNumber = embassyProcess.enjazNumber;
    stampedDate = embassyProcess.stampedDate;
  }
  let flightDate, arrivedDate;
  if (flight) {
    flightDate = flight.flightDateTime;
    arrivedDate = flight.arrivedDateTime;
  }
  let city, region, telephone, alternateTelephone, employAddress, country;
  if (address) {
    city = address.city;
    region = address.region;
    telephone = address.telephone;
    alternateTelephone = address.alternateTelephone;
    employAddress = address.streetAddress;
    country = address.country;
  }

  let displayName,
    kinship,
    cityC,
    telephoneC,
    addressC,
    alternateTelephoneC,
    countryC;

  if (contactPerson) {
    displayName = contactPerson.displayName;
    kinship = contactPerson.kinship;
    if (contactPerson.address) {
      cityC = contactPerson.address.city;
      addressC = contactPerson.address.streetAddress;
      telephoneC = contactPerson.address.telephone;
      alternateTelephoneC = contactPerson.address.alternateTelephone;
      countryC = contactPerson.address.country;
    }
  }
  let religion,
    nationality,
    pastNationality,
    maritalStatus,
    appliedCountry,
    appliedProfession,
    contractPeriod,
    complexion,
    documentReceivedDate,
    salary,
    currency,
    numberOfChildren,
    notes,
    height,
    weight,
    fullNameAmharic,
    lmisId,
    lmisPassword,
    labourId,
    musanedHeld;

  if (additionalData) {
    nationality = additionalData.nationality;
    pastNationality = additionalData.pastNationality;
    maritalStatus = additionalData.maritalStatus;
    religion = additionalData.religion;
    appliedCountry = additionalData.appliedCountry;
    appliedProfession = additionalData.appliedProfession;
    contractPeriod = additionalData.contractPeriod;
    complexion = additionalData.complexion;
    documentReceivedDate = additionalData.documentReceivedDate;
    salary = additionalData.salary;
    currency = additionalData.currency;
    numberOfChildren = additionalData.numberOfChildren;
    notes = additionalData.notes;
    height = additionalData.height;
    weight = additionalData.weight;
    fullNameAmharic = additionalData.fullNameAmharic;
    lmisId = additionalData.lmisId;
    lmisPassword = additionalData.lmisPassword;
    labourId = additionalData.labourId;
    musanedHeld = additionalData.musanedHeld;
  }

  let visaId,
    contractNumber,
    visaNumber,
    fullName,
    sponsorFullNameAmharic,
    cityAmharic,
    passportNumber,
    sponsorBirthDate,
    visaType;
  if (visa) {
    visaId = visa._id;
    contractNumber = visa.contractNumber;
    visaNumber = visa.visaNumber;
    fullName = visa.fullName;
    sponsorFullNameAmharic = visa.fullNameAmharic;
    cityAmharic = visa.cityAmharic;
    passportNumber = visa.passportNumber;
    sponsorBirthDate = visa.sponsorBirthDate;
    visaType = visa.visaType;
  }
  let sponsorTelephone, sponsorCity, sponsorCountry;
  if (visa && visa.address) {
    sponsorTelephone = visa.address.telephone;
    sponsorCity = visa.address.city;
    sponsorCountry = visa.address.country;
  }
  const {
    arabicLanguage,
    amharicLanguage,
    englishLanguage,
    cooking,
    educationLevel,
    skills,
    fieldOfStudy,
    yearCompleted,
    educationQG,
    remarks,
  } = (education ?? {}) as Education;
  const workExp = workExperience?.map((w) => {
    return { country: w.country, profession: w.profession, years: w.years };
  }) as [WorkExperience];
  const empSkills: [Skills] = skills?.map((sk) => sk) as [Skills];

  const prospect = prospects?.map((p) => {
    return {
      _id: p?._id,
      type: p?.type,
      displayName: p?.displayName,
      email: p?.email,
    };
  }) as [Company];

  const requiredDocuments: [RequiredDocuments] = documents?.map((sk) => sk) as [
    RequiredDocuments,
  ];
  const toDateAt2Pm = (value?: Date | string | null) => {
    if (!value) return undefined;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return undefined;
    parsed.setHours(14, 0, 0, 0);
    return parsed;
  };
  const toDateValue = (value?: Date | string | null) => {
    if (!value) return undefined;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  try {
    const response = await apolloClient.mutate({
      mutation: ADD_UPDATE_EMPLOYEE,
      variables: {
        _id: emp._id,
        status: emp.status,
        currentStatusDate: toDateValue(emp.currentStatusDate),
        currentStatusRemark: emp.currentStatusRemark,
        passportNumber: emp.passportNumber?.toUpperCase(),
        placeOfBirth: emp.placeOfBirth?.toUpperCase(),
        placeOfIssue: emp.placeOfIssue?.toUpperCase(),
        givenName: emp.givenName?.toUpperCase(),
        surName: emp.surName?.toUpperCase(),
        notificationMessage: emp.notificationMessage,
        medicalStatus: emp.medicalStatus,
        medicalBookedDate: toDateValue(emp.medicalBookedDate),
        medicalExpiryDate: toDateValue(emp.medicalExpiryDate),
        cocCompleted: emp.cocCompleted,
        biometricsCompleted: emp.biometricsCompleted,
        visaIssued: emp.visaIssued,
        visaIssuedDate: toDateValue(emp.visaIssuedDate),
        labourIssued: emp.labourIssued,
        lmisIssuedDate: toDateValue(emp.lmisIssuedDate),
        ticketBooked: emp.ticketBooked,
        sex: emp.sex,
        dateOfBirth: toDateAt2Pm(emp.dateOfBirth),
        selectionDueDate: toDateValue(emp.selectionDueDate),
        passportIssueDate: toDateAt2Pm(emp.passportIssueDate),
        passportExpiryDate: toDateAt2Pm(emp.passportExpiryDate),
        passportScan: emp.passportScan,
        documents: requiredDocuments,
        address: address && {
          region: region?.toUpperCase(),
          city: city?.toUpperCase(),
          telephone: telephone?.trim(),
          country,
          streetAddress: employAddress?.toUpperCase(),
          alternateTelephone: alternateTelephone?.trim(),
        },
        contactPerson: contactPerson && {
          displayName: displayName?.toUpperCase(),
          kinship: kinship?.toUpperCase(),
          address: contactPerson.address && {
            city: cityC?.toUpperCase(),
            streetAddress: addressC?.toUpperCase(),
            telephone: telephoneC?.trim(),
            alternateTelephone: alternateTelephoneC?.trim(),
            country: countryC,
          },
        },
        additionalData: additionalData && {
          nationality,
          pastNationality,
          maritalStatus,
          religion,
          appliedCountry,
          appliedProfession,
          contractPeriod,
          complexion,
          documentReceivedDate: toDateValue(documentReceivedDate),
          salary,
          currency,
          numberOfChildren,
          notes,
          height,
          weight,
          fullNameAmharic,
          lmisId,
          lmisPassword,
          labourId,
          musanedHeld,
        },
        education: {
          arabicLanguage,
          amharicLanguage,
          englishLanguage,
          cooking,
          educationLevel,
          skills: empSkills,
          fieldOfStudy,
          yearCompleted,
          educationQG,
          remarks,
        },
        workExperience: workExp,
        visa: emp.clearVisa
          ? undefined
          : visa && visa.visaNumber && visa.visaNumber.length >= 5
            ? {
                _id: visaId,
                contractNumber,
                visaNumber: visaNumber?.trim(),
                fullName: fullName?.toUpperCase(),
                fullNameAmharic: sponsorFullNameAmharic,
                cityAmharic,
                passportNumber: passportNumber?.trim(),
                sponsorBirthDate: sponsorBirthDate?.trim(),
                wekalaDate: "",
                visaType,
                address: visa.address && {
                  telephone: sponsorTelephone?.trim(),
                  city: sponsorCity?.toUpperCase(),
                  country: sponsorCountry,
                },
                owner: toCompanyInput(owner) ?? toCompanyInput(partner),
              }
            : undefined,
        embassyProcess: embassyProcess && {
          enjazNumber: enjazNumber?.trim().toUpperCase(),
          stampedDate: toDateValue(stampedDate),
        },
        flight: flight && {
          flightDateTime: toDateValue(flightDate),
          arrivedDateTime: toDateValue(arrivedDate),
        },
        owner: toCompanyInput(owner),
        partner: emp.clearVisa ? undefined : toCompanyInput(partner),
        parentCompany: toCompanyInput(parentCompany),
        prospects: prospect,
        clearVisa: emp.clearVisa === true ? true : undefined,
        calling: emp.calling,
      },
    });

    if (response && response.data && response.data.createEmployee) {
      evictEmployeesListCache();
      await setSuccessAction(dispatch, {
        message: "saved successfully",
      });
      return response.data.createEmployee as Employee;
    }
    //return [];
  } catch (error: any) {
    const message =
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.result?.errors?.[0]?.message ||
      error?.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const assignEmployeeVisa = createAsyncThunk<
  any,
  string[],
  { rejectValue: RejectWithValueType }
>("processes/assignEmployeeVisa", async (arg, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;

  try {
    console.log(arg);
    const response = await apolloClient.mutate({
      mutation: LINK_EMPLOYEE_VISA,
      variables: { ids: arg },
    });

    if (response && response.data && response.data.assignEmployeeVisa) {
      //return response.data.linkCompanies as Company;
      const linkedCompany = (await response.data
        .assignEmployeeVisa) as Employee;

      evictEmployeesListCache();
      await setSuccessAction(dispatch, {
        message: "Visa Successfully Linked",
        setupType: "Visa",
      });

      return linkedCompany;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const changeEmployeeStatus = createAsyncThunk<
  any,
  UpdateEmployeeStatusInput,
  { rejectValue: RejectWithValueType }
>("processes/changeEmployeeStatus", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const toDateValue = (value?: Date | string | null) => {
    if (!value) return undefined;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  try {
    const response = await apolloClient.mutate({
      mutation: UPDATE_EMPLOYEE_STATUS,
      variables: {
        _id: input._id,
        status: input.status,
        currentStatusDate: toDateValue(input.currentStatusDate),
        currentStatusRemark: input.currentStatusRemark,
        cocCompleted: input.cocCompleted,
        medicalStatus: input.medicalStatus,
        medicalBookedDate: toDateValue(input.medicalBookedDate),
        medicalExpiryDate: toDateValue(input.medicalExpiryDate),
        biometricsCompleted: input.biometricsCompleted,
        visaIssued: input.visaIssued,
        visaIssuedDate: toDateValue(input.visaIssuedDate),
        labourIssued: input.labourIssued,
        lmisIssuedDate: toDateValue(input.lmisIssuedDate),
        ticketBooked: input.ticketBooked,
        flight: input.flight && {
          flightDateTime: toDateValue(input.flight.flightDateTime),
          arrivedDateTime: toDateValue(input.flight.arrivedDateTime),
          ticketNumber: input.flight.ticketNumber,
          flightNumber: input.flight.flightNumber,
          ticketAmount: input.flight.ticketAmount,
          ticketCity: input.flight.ticketCity,
          departed: input.flight.departed,
          remarks: input.flight.remarks,
        },
      },
    });

    if (response?.data?.updateEmployeeStatus) {
      evictEmployeesListCache();
      await setSuccessAction(dispatch, {
        message: "saved successfully",
        setupType: "employeeStatus",
      });
      return response.data.updateEmployeeStatus as Employee;
    }
  } catch (error: any) {
    const message =
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.result?.errors?.[0]?.message ||
      error?.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const updateEmployeeFees = createAsyncThunk<
  any,
  UpdateEmployeeFeesInput,
  { rejectValue: RejectWithValueType }
>("processes/updateEmployeeFees", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: UPDATE_EMPLOYEE_FEES,
      variables: { input },
    });

    if (response?.data?.updateEmployeeFees) {
      evictEmployeesListCache();
      await setSuccessAction(dispatch, {
        message: "saved successfully",
        setupType: "employeeStatus",
      });
      return response.data.updateEmployeeFees as Employee;
    }
  } catch (error: any) {
    const message =
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.result?.errors?.[0]?.message ||
      error?.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const addEmployeeStatusRemark = createAsyncThunk<
  any,
  AddEmployeeStatusRemarkInput,
  { rejectValue: RejectWithValueType }
>("processes/addEmployeeStatusRemark", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: ADD_EMPLOYEE_STATUS_REMARK,
      variables: {
        _id: input._id,
        remark: input.remark,
        effectiveDate: input.effectiveDate,
      },
    });

    if (response?.data?.addEmployeeStatusRemark) {
      evictEmployeesListCache();
      await setSuccessAction(dispatch, {
        message: "saved successfully",
        setupType: "employeeStatus",
      });
      return response.data.addEmployeeStatusRemark as Employee;
    }
  } catch (error: any) {
    const message =
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.result?.errors?.[0]?.message ||
      error?.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const bulkUpdateEmployeeStatus = createAsyncThunk<
  BulkStatusUpdateResult,
  BulkUpdateEmployeeStatusInput,
  { rejectValue: RejectWithValueType }
>("processes/bulkUpdateEmployeeStatus", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const toDateValue = (value?: Date | string | null) => {
    if (!value) return undefined;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  try {
    const response = await apolloClient.mutate({
      mutation: BULK_UPDATE_EMPLOYEE_STATUS,
      variables: {
        ids: input.ids,
        status: input.status,
        currentStatusDate: toDateValue(input.currentStatusDate),
        currentStatusRemark: input.currentStatusRemark,
      },
    });

    const result = response?.data?.bulkUpdateEmployeeStatus as
      | BulkStatusUpdateResult
      | undefined;
    if (result) {
      evictEmployeesListCache();
      await setSuccessAction(dispatch, {
        message:
          result.failed > 0
            ? `${result.updated} updated, ${result.failed} failed`
            : `${result.updated} updated successfully`,
        setupType: "employeeStatus",
      });
      return result;
    }
    return rejectWithValue({ message: "Bulk status update failed" });
  } catch (error: any) {
    const message =
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.result?.errors?.[0]?.message ||
      error?.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const updateBookingFollowUp = createAsyncThunk<
  Employee,
  UpdateBookingFollowUpInput,
  { rejectValue: RejectWithValueType }
>("processes/updateBookingFollowUp", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const toDateValue = (value?: Date | string | null) => {
    if (!value) return undefined;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  try {
    const response = await apolloClient.mutate({
      mutation: UPDATE_BOOKING_FOLLOW_UP,
      variables: {
        employeeId: input.employeeId,
        type: input.type,
        status: input.status,
        scheduledDate: toDateValue(input.scheduledDate),
        remark: input.remark,
      },
    });

    if (response?.data?.updateBookingFollowUp) {
      evictEmployeesListCache();
      await setSuccessAction(dispatch, {
        message: "Follow-up updated",
        setupType: "employeeStatus",
      });
      return response.data.updateBookingFollowUp as Employee;
    }
    return rejectWithValue({ message: "Follow-up update failed" });
  } catch (error: any) {
    const message =
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.result?.errors?.[0]?.message ||
      error?.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const bulkUpdateBookingFollowUp = createAsyncThunk<
  BulkBookingFollowUpResult,
  BulkUpdateBookingFollowUpInput,
  { rejectValue: RejectWithValueType }
>("processes/bulkUpdateBookingFollowUp", async (input, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const toDateValue = (value?: Date | string | null) => {
    if (!value) return undefined;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  try {
    const response = await apolloClient.mutate({
      mutation: BULK_UPDATE_BOOKING_FOLLOW_UP,
      variables: {
        ids: input.ids,
        type: input.type,
        status: input.status,
        scheduledDate: toDateValue(input.scheduledDate),
        remark: input.remark,
      },
    });

    const result = response?.data?.bulkUpdateBookingFollowUp as
      | BulkBookingFollowUpResult
      | undefined;
    if (result) {
      evictEmployeesListCache();
      await setSuccessAction(dispatch, {
        message:
          result.failed > 0
            ? `${result.updated} updated, ${result.failed} failed`
            : `${result.updated} updated successfully`,
        setupType: "employeeStatus",
      });
      return result;
    }
    return rejectWithValue({ message: "Bulk follow-up update failed" });
  } catch (error: any) {
    const message =
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.result?.errors?.[0]?.message ||
      error?.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const checkWafidMedical = createAsyncThunk<
  WafidMedicalCheckResult,
  string,
  { rejectValue: RejectWithValueType }
>("processes/checkWafidMedical", async (employeeId, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: CHECK_WAFID_MEDICAL,
      variables: { employeeId },
    });
    const result = response?.data?.checkWafidMedical as
      | WafidMedicalCheckResult
      | undefined;
    if (!result) {
      return rejectWithValue({ message: "WAFID check failed" });
    }
    if (result.updated) {
      evictEmployeesListCache();
    }
    const message = result.error
      ? result.error
      : result.updated
        ? `WAFID: ${result.wafidStatus ?? result.medicalStatus ?? "updated"}`
        : `WAFID: ${result.wafidStatus ?? "no change"}`;
    if (result.error) {
      await setErrorAction(dispatch, { message });
    } else {
      await setSuccessAction(dispatch, {
        message,
        setupType: "employeeStatus",
      });
    }
    return result;
  } catch (error: any) {
    const message =
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.result?.errors?.[0]?.message ||
      error?.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const bulkCheckWafidMedical = createAsyncThunk<
  WafidMedicalBulkResult,
  string[],
  { rejectValue: RejectWithValueType }
>("processes/bulkCheckWafidMedical", async (employeeIds, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: BULK_CHECK_WAFID_MEDICAL,
      variables: { employeeIds },
    });
    const result = response?.data?.bulkCheckWafidMedical as
      | WafidMedicalBulkResult
      | undefined;
    if (!result) {
      return rejectWithValue({ message: "Bulk WAFID check failed" });
    }
    if (result.updated > 0) {
      evictEmployeesListCache();
    }
    await setSuccessAction(dispatch, {
      message:
        result.failed > 0
          ? `WAFID: ${result.updated} updated, ${result.failed} failed`
          : `WAFID: ${result.updated} updated`,
      setupType: "employeeStatus",
    });
    return result;
  } catch (error: any) {
    const message =
      error?.graphQLErrors?.[0]?.message ||
      error?.networkError?.result?.errors?.[0]?.message ||
      error?.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const checkStatusRecaptcha = createAsyncThunk<
  any,
  RecaptchaCheckStatus,
  { rejectValue: RejectWithValueType }
>("processes/checkStatusRecaptcha", async (captchaCheckStatus, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const { passportNumber, captcha } = captchaCheckStatus;

  try {
    const response = await apolloClient.query({
      query: CHECK_STATUS_CAPTCHA,
      variables: {
        passportNumber: passportNumber,
        captcha: captcha,
      },
      fetchPolicy: "no-cache",
    });

    if (response && response.data && response.data.recaptchaCheckStatus) {
      const recap = (await response.data.recaptchaCheckStatus) as Employee;
      await setSuccessAction(dispatch, {
        message: `Status found for ${recap.givenName || "employee"}`,
      });
      return recap;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
/** Get public base URL for uploaded files (API server without /graphql). */
export function getUploadBaseUrl(): string {
  const uri =
    process.env.REACT_APP_ENV === "development"
      ? process.env.REACT_APP_API_URI
      : process.env.REACT_APP_API_SERVER_URI;
  return (uri || "").replace(/\/graphql\/?$/i, "");
}

export const uploadCvPdf = createAsyncThunk<
  { publicUrl: string; avatar: string },
  { file: File; employeeId: string },
  { rejectValue: RejectWithValueType }
>("processes/uploadCvPdf", async ({ file, employeeId }, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: UPLOAD_FILE,
      variables: {
        _id: employeeId,
        type: PhotoTypes.Cv,
        avatar: file,
      },
    });
    const result = response?.data?.uploadFile;
    if (!result?.avatar) {
      await setErrorAction(dispatch, { message: "Upload failed" });
      return rejectWithValue({ message: "Upload failed" });
    }
    const baseUrl = getUploadBaseUrl();
    const publicUrl = baseUrl ? `${baseUrl}/${result.avatar}` : result.avatar;
    return { publicUrl, avatar: result.avatar };
  } catch (error: any) {
    const message = error?.message || "Failed to upload PDF";
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const sendWhatsAppDocument = createAsyncThunk<
  { success: boolean; messageId?: string },
  { to: string; pdfUrl: string; filename?: string },
  { rejectValue: RejectWithValueType }
>(
  "processes/sendWhatsAppDocument",
  async ({ to, pdfUrl, filename }, thunkAPI) => {
    const { rejectWithValue, dispatch } = thunkAPI;
    try {
      const response = await apolloClient.mutate({
        mutation: SEND_WHATSAPP_DOCUMENT,
        variables: {
          input: { to, pdfUrl, filename: filename || "document.pdf" },
        },
      });
      const result = response?.data?.sendWhatsAppDocument;
      if (!result?.success) {
        await setErrorAction(dispatch, {
          message: "Failed to send via WhatsApp",
        });
        return rejectWithValue({ message: "Failed to send via WhatsApp" });
      }
      await setSuccessAction(dispatch, { message: "CV sent via WhatsApp" });
      return result;
    } catch (error: any) {
      const message =
        error?.graphQLErrors?.[0]?.message ||
        error?.message ||
        "Failed to send via WhatsApp";
      await setErrorAction(dispatch, { message });
      return rejectWithValue({ message });
    }
  },
);

export const clearEmployeeAttachment = createAsyncThunk<
  any,
  { employeeId: string; type: PhotoTypes; silent?: boolean },
  { rejectValue: RejectWithValueType }
>(
  "processes/clearEmployeeAttachment",
  async ({ employeeId, type, silent }, thunkAPI) => {
    const { rejectWithValue, dispatch } = thunkAPI;
    try {
      const response = await apolloClient.mutate({
        mutation: CLEAR_EMPLOYEE_ATTACHMENT,
        variables: { employeeId, type },
      });
      if (response?.data?.clearEmployeeAttachment) {
        if (!silent) {
          await setSuccessAction(dispatch, {
            message: "Attachment removed",
          });
        }
        return response.data.clearEmployeeAttachment as Employee;
      }
      const message = "Remove attachment failed";
      await setErrorAction(dispatch, { message });
      return rejectWithValue({ message });
    } catch (error: any) {
      const message = error.message;
      await setErrorAction(dispatch, { message });
      return rejectWithValue({ message });
    }
  },
);

export const uploadPicture = createAsyncThunk<
  any,
  UploadGraphQL,
  { rejectValue: RejectWithValueType }
>("processes/uploadPicture", async (uploadInput, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  const { _id, avatar, type, onProgress, silent } = uploadInput;
  try {
    const size = avatar?.size ?? 0;
    const maxBytes =
      type === PhotoTypes.Video
        ? MAX_EMPLOYEE_VIDEO_BYTES
        : MAX_EMPLOYEE_ATTACHMENT_BYTES;
    if (size > maxBytes) {
      const message =
        type === PhotoTypes.Video
          ? "Video size must not exceed 50 MB"
          : "File size must not exceed 5 MB";
      await setErrorAction(dispatch, { message });
      return rejectWithValue({ message });
    }

    const response = await apolloClient.mutate({
      mutation: SINGLE_UPLOAD_MUTATION,
      variables: {
        _id: _id,
        type: type,
        avatar: avatar,
      },
      context: onProgress
        ? { fetchOptions: { onUploadProgress: onProgress } }
        : undefined,
    });

    if (response?.data?.uploadPicture) {
      if (!silent) {
        await setSuccessAction(dispatch, {
          message: "uploaded successfully",
        });
      }
      return response.data.uploadPicture as UploadGraphQLResult;
    }

    const message = "Upload failed: empty server response";
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const removeEmployee = createAsyncThunk<
  any,
  string,
  { rejectValue: RejectWithValueType }
>("processes/removeEmployee", async (id, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: REMOVE_EMPLOYEE,
      variables: { id },
    });

    if (response && response.data && response.data.deleteEmployee) {
      evictEmployeesListCache();
      await setSuccessAction(dispatch, {
        message: "Employee Successfully Deleted",
        setupType: "Employee",
      });
      return id as string;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});

export const fetchVisas = createAsyncThunk<
  any,
  VisaArgs,
  { rejectValue: RejectWithValueType }
>("processes/fetchVisas", async (visaArgs, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;

  try {
    const { refreshList } = visaArgs;
    const fetchPolicy =
      refreshList === "refresh" ? "network-only" : "cache-first";
    const response = await apolloClient.query({
      query: GET_ALL_VISAS,
      variables: { ...visaArgs },
      fetchPolicy,
    });

    if (response && response.data && response.data.visas) {
      return response.data.visas as Visa[];
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const getVisa = createAsyncThunk<
  any,
  string,
  { rejectValue: RejectWithValueType }
>("processes/getVisa", async (visaId, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.query({
      query: GET_SELECTED_VISA,
      variables: { id: visaId },
    });
    if (response && response.data && response.data.getVisa) {
      return response.data.getVisa as Visa;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const createVisa = createAsyncThunk<
  any,
  Visa,
  { rejectValue: RejectWithValueType }
>("processes/createVisa", async (vi, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;

  const visa = { ...vi } as Visa;
  const { address, owner } = visa;
  const { city, telephone, country } = address as Address;
  //console.log(visa);
  try {
    const response = await apolloClient.mutate({
      mutation: ADD_UPDATE_VISA,
      variables: {
        _id: visa._id,
        passportNumber: visa.passportNumber,
        fullName: visa.fullName,
        visaNumber: visa.visaNumber,
        visaQty: 1,
        firstTime: visa.firstTime,
        goodLooking: visa.goodLooking,
        writeRead: visa.writeRead,
        profession: visa.profession,
        religion: visa.religion,
        ageCategory: visa.ageCategory,
        complexion: visa.complexion,
        salary: visa.salary,
        currency: visa.currency,
        remarks: visa.remarks,
        status: VisaStatus.new,
        visaType: visa.visaType,
        address: { city, telephone, country },
        owner: {
          _id: owner?._id,
          type: owner?.type,
          displayName: owner?.displayName,
          email: owner?.email,
        },
      },
    });

    if (response && response.data && response.data.createVisa) {
      await setSuccessAction(dispatch, {
        message: "saved successfully",
      });
      return response.data.createVisa as Visa;
    }
    //return [];
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const removeVisa = createAsyncThunk<
  any,
  string,
  { rejectValue: RejectWithValueType }
>("setups/removeVisa", async (id, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: REMOVE_VISA,
      variables: { id },
    });

    if (response && response.data && response.data.deleteVisa) {
      await setSuccessAction(dispatch, {
        message: "Visa Successfully Removed",
        setupType: "Visa",
      });
      return id as string;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const fetchNotifications = createAsyncThunk<
  any,
  NotificationArgs,
  { rejectValue: RejectWithValueType }
>("processes/fetchNotifications", async (notificationArgs, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;

  try {
    //const { refreshList } = notificationArgs;
    const fetchPolicy = "network-only";
    //refreshList === "refresh" ? "network-only" : "cache-first";
    const response = await apolloClient.query({
      query: GET_ALL_NOTIFICATIONS,
      variables: { ...notificationArgs },
      fetchPolicy,
    });

    if (response && response.data && response.data.findNotifications) {
      return response.data.findNotifications as NotificationsWithCount;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const fetchPopupNotifications = createAsyncThunk<
  NotificationsWithCount,
  void,
  { rejectValue: RejectWithValueType }
>("processes/fetchPopupNotifications", async (_, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;

  try {
    const response = await apolloClient.query({
      query: GET_ALL_NOTIFICATIONS,
      variables: {
        skip: 0,
        take: 10,
        status: NotificationStatus.new,
        audience: NotificationAudience.wall,
      },
      fetchPolicy: "network-only",
    });

    if (response?.data?.findNotifications) {
      return response.data.findNotifications as NotificationsWithCount;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
  return rejectWithValue({ message: "Failed to fetch popup notifications" });
});
export const removeNotification = createAsyncThunk<
  any,
  string,
  { rejectValue: RejectWithValueType }
>("processes/removeNotification", async (id, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: REMOVE_NOTIFICATION,
      variables: { id },
    });

    if (response && response.data && response.data.deleteNotification) {
      await setSuccessAction(dispatch, {
        message: "Notification Successfully Deleted",
        setupType: "Notification",
      });
      return id as string;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const createNotification = createAsyncThunk<
  any,
  Notification,
  { rejectValue: RejectWithValueType }
>("processes/createNotification", async (notify, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;

  //const notify = { ...vi } as Notification;
  //const { employee } = notify;
  // console.log(notify);
  try {
    const response = await apolloClient.mutate({
      mutation: ADD_UPDATE_NOTIFICATION,
      variables: {
        _id: notify._id,
        message: notify.message,
        priority: notify.priority,
        status: notify.status,
      },
    });

    if (response && response.data && response.data.createNotification) {
      await setSuccessAction(dispatch, {
        message: "saved successfully",
      });
      return response.data.createNotification as Notification;
    }
    //return [];
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
});
export const markAsReadNotification = createAsyncThunk<
  string,
  string,
  { rejectValue: RejectWithValueType }
>("processes/markAsReadNotification", async (notificationId, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: MARK_AS_READ_NOTIFICATION,
      variables: { id: notificationId },
    });
    if (response?.data?.markNotificationAsRead) {
      return response.data.markNotificationAsRead as string;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
  return rejectWithValue({ message: "Failed to mark notification as read" });
});

export const markAllAsReadNotifications = createAsyncThunk<
  number,
  void,
  { rejectValue: RejectWithValueType }
>("processes/markAllAsReadNotifications", async (_, thunkAPI) => {
  const { rejectWithValue, dispatch } = thunkAPI;
  try {
    const response = await apolloClient.mutate({
      mutation: MARK_ALL_AS_READ_NOTIFICATIONS,
    });
    if (response?.data?.markAllNotificationsAsRead != null) {
      return response.data.markAllNotificationsAsRead as number;
    }
  } catch (error: any) {
    const message = error.message;
    await setErrorAction(dispatch, { message });
    return rejectWithValue({ message });
  }
  return rejectWithValue({ message: "Failed to mark all as read" });
});
async function setSuccessAction(
  dispatch: ThunkDispatch<any, any, any>,
  payload: any,
) {
  dispatch(setSuccess(payload));
  setTimeout(() => {
    switch (payload.setupType) {
      case "Employee":
        dispatch(resetSelectedEmployee());
        break;
      case "Visa":
        dispatch(resetSelectedVisa());
        break;
    }
    dispatch(resetSuccess());
  }, 2000);
}
async function setErrorAction(
  dispatch: ThunkDispatch<any, any, any>,
  payload: any,
) {
  dispatch(setError(payload));
  setTimeout(() => {
    dispatch(resetError());
  }, 6000);
}
const defaultSkills = [
  Skills.washing_dishes,
  Skills.washing_ironing_clothes,
  Skills.dusting_cleaning,
  Skills.baby_sitting,
  Skills.children_care,
];
const defaultEmployee: Employee = {
  givenName: "",
  surName: "",
  sex: Sex.female,
  status: EmployeeStatus.new,

  cocCompleted: false,
  medicalStatus: MedicalStatus.not_done,
  biometricsCompleted: false,
  visaIssued: false,
  labourIssued: false,
  ticketBooked: false,
  placeOfBirth: "",
  placeOfIssue: "ADDIS ABABA",
  passportNumber: "",
  dateOfBirth: addYears(new Date(), -25),
  passportIssueDate: new Date(),
  passportExpiryDate: addYears(new Date(), 5),
  documents: [RequiredDocuments.passport],
  address: {
    region: "",
    city: "",
    telephone: "",
    alternateTelephone: "",
    streetAddress: "",
    country: CountriesList.ethiopia,
  },
  contactPerson: {
    displayName: "",
    kinship: "",
    address: {
      city: "",
      telephone: "",
      alternateTelephone: "",
      streetAddress: "",
      country: CountriesList.ethiopia,
    },
  },
  additionalData: {
    nationality: Nationalities.ethiopian,
    pastNationality: Nationalities.ethiopian,
    maritalStatus: MaritalStatus.single,
    religion: ReligionTypes.muslim,
    appliedCountry: CountriesList.saudi_arabia,
    appliedProfession: ProfessionalTypes.housemaid,
    contractPeriod: ExperienceYears.two,
    complexion: Complexion.brown,
    salary: 1000,
    currency: CurrencyTypes.saudi_riyal,
    numberOfChildren: 0,
    height: "",
    weight: "",
    notes: "",
  },
  education: {
    arabicLanguage: LanguageExperience.poor,
    amharicLanguage: LanguageExperience.poor,
    cooking: CookingExperience.poor,
    englishLanguage: LanguageExperience.poor,
    educationLevel: EducationLevel.grade_eight_complete,
    skills: defaultSkills,
    educationQG: "",
    fieldOfStudy: "",
    yearCompleted: "",
    remarks: "",
  },
};
const defaultVisa: Visa = {
  visaNumber: "",
  passportNumber: "",
  fullName: "",
  wekalaDate: "",
  address: { city: "", telephone: "", country: CountriesList.saudi_arabia },
  profession: ProfessionalTypes.housemaid,
  religion: ReligionTypes.muslim,
  ageCategory: AgeCategory.bet2125,
  complexion: Complexion.acceptable,
  salary: 1000,
  currency: CurrencyTypes.saudi_riyal,
  remarks: "",
  firstTime: false,
  goodLooking: false,
  writeRead: false,
};

const defaultShortLink: ShortLink = {
  link: "",
};
const defaultCompany: Company = {
  displayName: "",
  email: "",
  licenseNumber: "",
  address: {
    mobile: "",
    telephone: "",
    email: "",
    country: CountriesList.ethiopia,
  },
};
const initialProcessesState: ProcessState = {
  employeesWithCount: {
    totalCount: 0,
    employees: [],
    header: { ...defaultCompany },
  },
  visasWithCount: { totalCount: 0, visas: [] },
  notificationsWithCount: { totalCount: 0, notifications: [] },
  popupNotificationsWithCount: { totalCount: 0, notifications: [] },
  sharedLinks: [],
  selectedEmployee: { ...defaultEmployee },
  selectedVisa: { ...defaultVisa },
  selectedShortLink: { ...defaultShortLink },
  shortLinkHeader: { ...defaultCompany },
  employeeListsStale: false,
  listLoading: "idle",
  listRequestId: undefined,
  loading: "idle",
  currentRequestId: undefined,
  success: null,
  error: null,
};

/** Reducers must stay pure: thunks evict the Apollo cache, reducers only set the flag. */
function markEmployeeListsStale(state: ProcessState) {
  state.employeeListsStale = true;
}

function notificationMessage(
  state: ProcessState,
  id: string,
): string | undefined {
  return (
    state.notificationsWithCount.notifications.find((h) => h._id === id)
      ?.message ??
    state.popupNotificationsWithCount.notifications.find((h) => h._id === id)
      ?.message
  );
}

function removeNotificationFromSlices(state: ProcessState, id: string) {
  const msg = notificationMessage(state, id);

  const inPageList = state.notificationsWithCount.notifications.some(
    (h) => h._id === id,
  );
  state.notificationsWithCount.notifications =
    state.notificationsWithCount.notifications.filter((h) => h._id !== id);
  if (inPageList) {
    state.notificationsWithCount.totalCount = Math.max(
      0,
      (state.notificationsWithCount.totalCount as number) - 1,
    );
  }

  const affectsPopup = !msg || !POPUP_EXCLUDED_MESSAGES.includes(msg);
  const inPopupList = state.popupNotificationsWithCount.notifications.some(
    (h) => h._id === id,
  );
  state.popupNotificationsWithCount.notifications =
    state.popupNotificationsWithCount.notifications.filter((h) => h._id !== id);
  if (affectsPopup && (inPopupList || Boolean(msg))) {
    state.popupNotificationsWithCount.totalCount = Math.max(
      0,
      (state.popupNotificationsWithCount.totalCount as number) - 1,
    );
  }
}

/** Merge mutation result into the matching row of the current list (no refetch). */
function patchEmployeeInList(state: ProcessState, employee: Employee) {
  if (!employee?._id) return;
  const id = String(employee._id);
  const list = state.employeesWithCount.employees;
  const idx = list.findIndex((e) => e?._id != null && String(e._id) === id);
  if (idx === -1) return;
  list[idx] = { ...list[idx], ...employee };
}

export const setupsSlice = createSlice({
  name: "processes",
  initialState: initialProcessesState,
  reducers: {
    setSuccess: (state, { payload }) => {
      state.success = payload;
    },
    resetSuccess: (state) => {
      state.success = null;
    },
    setError: (state, { payload }) => {
      state.error = payload;
      if (payload.message.includes("authorized")) {
        localStorage.removeItem("userInfo");
      }
    },
    resetError: (state) => {
      state.error = null;
    },
    resetSelectedEmployee: (state) => {
      state.selectedEmployee = {
        ...defaultEmployee,
      };
    },
    setSelectedEmployee: (state, { payload }) => {
      state.selectedEmployee = payload;
    },
    resetSelectedVisa: (state) => {
      state.selectedVisa = {
        ...defaultVisa,
      };
    },
    setSelectedVisa: (state, { payload }) => {
      state.selectedVisa = payload;
    },

    setEmployees: (state, { payload }) => {
      state.employeesWithCount = payload;
    },
    clearEmployeeListsStale: (state) => {
      state.employeeListsStale = false;
    },
    /** Drop one row from the current list (e.g. status no longer matches the active tab filter). */
    removeEmployeeFromListById: (state, { payload }: { payload: string }) => {
      const id = String(payload);
      const list = state.employeesWithCount.employees;
      if (!list?.length) {
        return;
      }
      const idx = list.findIndex((e) => e?._id != null && String(e._id) === id);
      if (idx === -1) {
        return;
      }
      list.splice(idx, 1);
      const tc = state.employeesWithCount.totalCount;
      if (typeof tc === "number" && tc > 0) {
        state.employeesWithCount.totalCount = tc - 1;
      }
    },
    setVisas: (state, { payload }) => {
      state.visasWithCount = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sharedEmployees.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(sharedEmployees.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.employeesWithCount.employees = payload.employees;
      state.employeesWithCount.totalCount = payload.totalCount;
      state.employeesWithCount.header = payload.header;
    });
    builder.addCase(sharedEmployees.rejected, (state) => {
      state.loading = "idle";
    });
    builder.addCase(linkedEmployees.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(linkedEmployees.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.employeesWithCount = payload;
    });
    builder.addCase(linkedEmployees.rejected, (state) => {
      state.loading = "idle";
    });
    builder.addCase(shareByEmail.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(shareByEmail.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.success = { message: "Email Sent Successfully" };
    });
    builder.addCase(shareByEmail.rejected, (state) => {
      state.loading = "idle";
    });
    builder.addCase(generateLink.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(generateLink.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      //state.success = payload;
      state.selectedShortLink = payload;
      state.sharedLinks.unshift(payload);
    });
    builder.addCase(generateLink.rejected, (state) => {
      state.loading = "idle";
    });
    builder.addCase(fetchShortLinks.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(fetchShortLinks.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.sharedLinks = payload;
    });
    builder.addCase(fetchShortLinks.rejected, (state) => {
      state.loading = "idle";
    });
    builder.addCase(fetchNotifications.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(fetchNotifications.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.notificationsWithCount = payload;
    });
    builder.addCase(fetchNotifications.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(fetchPopupNotifications.fulfilled, (state, { payload }) => {
      state.popupNotificationsWithCount = payload;
    });

    builder.addCase(fetchEmployees.pending, (state, { meta }) => {
      state.listLoading = "pending";
      state.listRequestId = meta.requestId;
    });
    builder.addCase(fetchEmployees.fulfilled, (state, { payload, meta }) => {
      // Ignore out-of-order responses: only the latest dispatched request may
      // write the list, so a slow fetch can't overwrite newer filter results.
      if (meta.requestId !== state.listRequestId) return;
      state.listLoading = "idle";
      state.employeesWithCount = payload;
    });
    builder.addCase(fetchEmployees.rejected, (state, { meta }) => {
      if (meta.requestId !== state.listRequestId) return;
      state.listLoading = "idle";
    });

    builder.addCase(getEmployee.fulfilled, (state, { payload }) => {
      state.selectedEmployee = payload;
    });
    builder.addCase(assignEmployeeVisa.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(assignEmployeeVisa.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.selectedEmployee = payload;
      markEmployeeListsStale(state);
    });
    builder.addCase(assignEmployeeVisa.rejected, (state) => {
      state.loading = "idle";
    });
    builder.addCase(changeEmployeeStatus.fulfilled, (state, { payload }) => {
      if (!payload?._id) {
        return;
      }
      state.selectedEmployee = payload;
      patchEmployeeInList(state, payload);
      markEmployeeListsStale(state);
    });
    builder.addCase(addEmployeeStatusRemark.fulfilled, (state, { payload }) => {
      if (!payload?._id) {
        return;
      }
      state.selectedEmployee = payload;
      patchEmployeeInList(state, payload);
      markEmployeeListsStale(state);
    });
    builder.addCase(updateEmployeeFees.fulfilled, (state, { payload }) => {
      if (!payload?._id) {
        return;
      }
      state.selectedEmployee = payload;
      patchEmployeeInList(state, payload);
      markEmployeeListsStale(state);
    });
    builder.addCase(checkStatusRecaptcha.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(checkStatusRecaptcha.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.selectedEmployee = payload;
    });
    builder.addCase(checkStatusRecaptcha.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(createEmployee.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(createEmployee.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.selectedEmployee = payload;
      markEmployeeListsStale(state);
    });
    builder.addCase(createEmployee.rejected, (state) => {
      state.loading = "idle";
    });
    builder.addCase(clearEmployeeAttachment.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(clearEmployeeAttachment.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      if (
        state.selectedEmployee &&
        payload &&
        payload._id === state.selectedEmployee._id
      ) {
        state.selectedEmployee = payload;
      }
    });
    builder.addCase(clearEmployeeAttachment.rejected, (state) => {
      state.loading = "idle";
    });
    builder.addCase(uploadPicture.fulfilled, (state, { payload }) => {
      if (!payload?.avatar || !state.selectedEmployee) {
        return;
      }
      if (
        payload._id != null &&
        state.selectedEmployee._id != null &&
        String(payload._id) !== String(state.selectedEmployee._id)
      ) {
        return;
      }
      const photoUrl = payload.avatar as string;
      const kind = String(payload.type ?? "");

      switch (kind) {
        case PhotoTypes.Face:
          state.selectedEmployee.facePhoto = photoUrl;
          break;
        case PhotoTypes.Stand:
          state.selectedEmployee.standPhoto = photoUrl;
          break;
        case PhotoTypes.Medical:
          state.selectedEmployee.medicalPhoto = photoUrl;
          break;
        case PhotoTypes.Passport:
          state.selectedEmployee.passportScan = photoUrl;
          break;
        case PhotoTypes.OriginalId:
          state.selectedEmployee.originalIdScan = photoUrl;
          break;
        case PhotoTypes.EmergencyContactId:
          state.selectedEmployee.emergencyContactIdScan = photoUrl;
          break;
        case PhotoTypes.Insurance:
          state.selectedEmployee.insuranceScan = photoUrl;
          break;
        case PhotoTypes.CocCertificate:
          state.selectedEmployee.cocCertificateScan = photoUrl;
          break;
        case PhotoTypes.EducationCertificate:
          state.selectedEmployee.educationCertificateScan = photoUrl;
          break;
        case PhotoTypes.FingerPrint:
          state.selectedEmployee.fingerPrintScan = photoUrl;
          break;
        case PhotoTypes.EmploymentContract:
          state.selectedEmployee.employmentContractScan = photoUrl;
          break;
        case PhotoTypes.WorkingVisa:
          state.selectedEmployee.workingVisaScan = photoUrl;
          break;
        case PhotoTypes.FlightTicket:
          state.selectedEmployee.flightTicketScan = photoUrl;
          break;
        case PhotoTypes.Video:
          state.selectedEmployee.employeeVideo = photoUrl;
          break;
        case PhotoTypes.FeeReceiptCoc:
        case PhotoTypes.FeeReceiptEmbassy:
        case PhotoTypes.FeeReceiptFlight:
        case PhotoTypes.FeeReceiptOther: {
          const section = (
            {
              [PhotoTypes.FeeReceiptCoc]: "coc",
              [PhotoTypes.FeeReceiptEmbassy]: "embassy",
              [PhotoTypes.FeeReceiptFlight]: "flight",
              [PhotoTypes.FeeReceiptOther]: "other",
            } as const
          )[kind];
          state.selectedEmployee.fees = {
            ...state.selectedEmployee.fees,
            [section]: {
              ...state.selectedEmployee.fees?.[section],
              receiptScan: photoUrl,
            },
          };
          break;
        }
        default:
          break;
      }
      patchEmployeeInList(state, state.selectedEmployee);
    });
    builder.addCase(removeEmployee.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(removeEmployee.fulfilled, (state, { payload }) => {
      state.loading = "idle";

      state.employeesWithCount.totalCount =
        (state.employeesWithCount.totalCount as number) - 1;
      state.employeesWithCount.employees =
        state.employeesWithCount.employees.filter((h) => h._id !== payload);
      markEmployeeListsStale(state);
    });
    builder.addCase(removeEmployee.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(fetchVisas.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(fetchVisas.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.visasWithCount = payload;
    });
    builder.addCase(fetchVisas.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(getVisa.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(getVisa.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.selectedVisa = payload;
    });
    builder.addCase(getVisa.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(createVisa.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(createVisa.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      state.selectedVisa = payload;
      state.visasWithCount.visas = state.visasWithCount.visas?.filter(
        (c) => c._id !== payload._id,
      );
      state.visasWithCount.visas.unshift(payload);
      state.visasWithCount.totalCount++;
    });
    builder.addCase(createVisa.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(removeVisa.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(removeVisa.fulfilled, (state, { payload }) => {
      state.loading = "idle";

      state.visasWithCount.totalCount =
        (state.visasWithCount.totalCount as number) - 1;
      state.visasWithCount.visas = state.visasWithCount.visas.filter(
        (h) => h._id !== payload,
      );
    });
    builder.addCase(removeVisa.rejected, (state) => {
      state.loading = "idle";
    });
    builder.addCase(removeNotification.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(removeNotification.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      removeNotificationFromSlices(state, payload);
    });
    builder.addCase(removeNotification.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(markAsReadNotification.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(markAsReadNotification.fulfilled, (state, { payload }) => {
      state.loading = "idle";
      removeNotificationFromSlices(state, payload);
    });
    builder.addCase(markAsReadNotification.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(markAllAsReadNotifications.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(markAllAsReadNotifications.fulfilled, (state) => {
      state.loading = "idle";
      state.notificationsWithCount.totalCount = 0;
      state.notificationsWithCount.notifications = [];
      state.popupNotificationsWithCount.totalCount = 0;
      state.popupNotificationsWithCount.notifications = [];
    });
    builder.addCase(markAllAsReadNotifications.rejected, (state) => {
      state.loading = "idle";
    });

    builder.addCase(createNotification.pending, (state) => {
      state.loading = "pending";
    });
    builder.addCase(createNotification.fulfilled, (state, { payload }) => {
      state.loading = "idle";

      state.notificationsWithCount.totalCount =
        (state.notificationsWithCount.totalCount as number) - 1;
      state.notificationsWithCount.notifications =
        state.notificationsWithCount.notifications.filter(
          (h) => h._id !== payload._id,
        );
    });
    builder.addCase(createNotification.rejected, (state) => {
      state.loading = "idle";
    });

    // builder.addCase(addEmployeeVisa.pending, (state) => {
    //   state.loading = "pending";
    // });
    // builder.addCase(addEmployeeVisa.fulfilled, (state, { payload }) => {
    //   state.loading = "idle";
    //   state.selectedEmployee = payload;
    // });
    // builder.addCase(addEmployeeVisa.rejected, (state) => {
    //   state.loading = "idle";
    // });
  },
});
const { actions, reducer } = setupsSlice;
export const {
  resetSuccess,
  setSuccess,
  resetError,
  setError,

  setSelectedEmployee,
  resetSelectedEmployee,
  setSelectedVisa,
  resetSelectedVisa,
  setEmployees,
  clearEmployeeListsStale,
  removeEmployeeFromListById,
  setVisas,
} = actions;

export default reducer;

// Selectors
export const selectProcesses = (state: RootState) =>
  state.processes as ProcessState;
